import CryptoJS from "crypto-js";

/**
 * Decrypts AES-256-CBC encrypted data that was encrypted using the Python backend
 *
 * The Python encryption process:
 * 1. Uses AES-256-CBC with PKCS7 padding
 * 2. Generates random 16-byte IV
 * 3. Combines IV + encrypted data
 * 4. Base64 encodes the result
 *
 * @param encryptedData - Base64 encoded encrypted string from API
 * @param aesKey - The AES encryption key (same as TENANT_REPORT_AES_KEY)
 * @returns Decrypted JSON object
 */
export const decryptTenantReportData = (
  encryptedData: string,
  aesKey: string,
): any => {
  try {
    // Validate inputs
    if (!encryptedData || !aesKey) {
      throw new Error("Missing encrypted data or AES key");
    }

    // Ensure key is 32 bytes (256 bits) - same logic as Python
    let keyBytes = CryptoJS.enc.Utf8.parse(aesKey);
    if (keyBytes.sigBytes < 32) {
      throw new Error("Encryption key must be at least 32 characters long");
    } else if (keyBytes.sigBytes > 32) {
      // Truncate if longer than 32 bytes - same as Python
      const truncatedKey = aesKey.substring(0, 32);
      keyBytes = CryptoJS.enc.Utf8.parse(truncatedKey);
    }

    // Decode base64 to get IV + encrypted data
    const combinedBytes = CryptoJS.enc.Base64.parse(encryptedData);

    // Extract IV (first 16 bytes) and encrypted data (remaining bytes)
    const iv = CryptoJS.lib.WordArray.create(combinedBytes.words.slice(0, 4)); // 16 bytes = 4 words
    const encryptedBytes = CryptoJS.lib.WordArray.create(
      combinedBytes.words.slice(4),
      combinedBytes.sigBytes - 16,
    );

    // Decrypt using AES-256-CBC
    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: encryptedBytes } as any,
      keyBytes,
      {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      },
    );

    // Convert decrypted bytes to UTF-8 string
    const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);

    if (!decryptedString) {
      throw new Error("Decryption failed - invalid key or corrupted data");
    }

    // Parse JSON

    return JSON.parse(decryptedString);
  } catch (error: any) {
    console.error("Decryption failed:", error);
    throw new Error(
      `Data decryption failed: ${error?.message || "Unknown error"}`,
    );
  }
};

/**
 * Alternative decryption method using Web Crypto API (more modern approach)
 * Note: This requires the key to be available in the frontend, which may not be ideal for security
 */
export const decryptTenantReportDataWebCrypto = async (
  encryptedData: string,
  aesKey: string,
): Promise<any> => {
  try {
    // Check if Web Crypto API is available
    if (!window.crypto || !window.crypto.subtle) {
      throw new Error("Web Crypto API not available");
    }

    // Prepare key - ensure 32 bytes
    let keyString = aesKey;
    if (keyString.length < 32) {
      throw new Error("Encryption key must be at least 32 characters long");
    } else if (keyString.length > 32) {
      keyString = keyString.substring(0, 32);
    }

    // Convert key to ArrayBuffer
    const keyBuffer = new TextEncoder().encode(keyString);

    // Import the key
    const cryptoKey = await window.crypto.subtle.importKey(
      "raw",
      keyBuffer,
      { name: "AES-CBC" },
      false,
      ["decrypt"],
    );

    // Decode base64
    const combinedBuffer = Uint8Array.from(atob(encryptedData), (c) =>
      c.charCodeAt(0),
    );

    // Extract IV (first 16 bytes) and encrypted data
    const iv = combinedBuffer.slice(0, 16);
    const encryptedBuffer = combinedBuffer.slice(16);

    // Decrypt
    const decryptedBuffer = await window.crypto.subtle.decrypt(
      { name: "AES-CBC", iv: iv },
      cryptoKey,
      encryptedBuffer,
    );

    // Convert to string and remove PKCS7 padding manually
    const decryptedArray = new Uint8Array(decryptedBuffer);
    const paddingLength = decryptedArray[decryptedArray.length - 1];
    const unpaddedArray = decryptedArray.slice(
      0,
      decryptedArray.length - paddingLength,
    );

    const decryptedString = new TextDecoder().decode(unpaddedArray);

    // Parse JSON
    const jsonData = JSON.parse(decryptedString);

    return jsonData;
  } catch (error: any) {
    console.error("Web Crypto decryption failed:", error);
    throw new Error(
      `Data decryption failed: ${error?.message || "Unknown error"}`,
    );
  }
};
